import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { MonitoreoUsuarioResponse } from "../../../../../modulos/configuracion/monitoreo/response/MonitoreoUsuarioResponse";
import { monitoreoUsuarioAccionesGrpcService } from "../../../../../modulos/configuracion/monitoreo/services/MonitoreoUsuarioAccionesGrpcService";
import { fx_createMonitoreoUsuarioGrpcService } from "../../../../../modulos/configuracion/monitoreo/services/MonitoreoUsuarioGrpcService";
import { formatFechaHoraAnioMesDia } from "../../../../../utils/fechaUtils";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { MonitorAltaModal } from "../modal/MonitorAltaModal";
import rowsTemplate from "./monitorRowTemplate.html";
import template from "./monitoresTemplate.html";

mustache.parse(template);
mustache.parse(rowsTemplate);

/** Título con el que se agrupan los avisos de esta sección en los toasts. */
const TOAST_TITULO: string = "Monitores";

/** Máscara del PIN mientras está oculto. */
const PIN_OCULTO: string = "••••";

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let MONITORES_ID: number = 0;

/** Iniciales para el avatar: primeras letras de las dos primeras palabras del
 *  nombre, o las dos primeras letras si es una sola palabra. */
const fx_iniciales = (nombre: string): string => {
  const palabras = nombre.trim().split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return "?";
  if (palabras.length === 1) return palabras[0].slice(0, 2).toUpperCase();
  return (palabras[0][0] + palabras[1][0]).toUpperCase();
};

/** Sección "Gestión de monitores": el listado de monitores de campo y, por cada
 *  uno, ver su PIN, activarlo/inactivarlo y reenviarle las credenciales. El
 *  alta se hace en `MonitorAltaModal`, que se abre desde el botón del header.
 *
 *  El alcance es el **cliente**, no la finca activa. No hay edición ni borrado:
 *  el server solo expone alta, activar, inactivar y reenviar. Todas las
 *  acciones son inmediatas y avisan por toast, como el resto del modal. */
export class MonitoresRender implements IConnectRender {
  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number = ++MONITORES_ID;

  /** Cliente dueño de los monitores (viene de la finca activa). */
  private readonly _clienteId: string;

  /** Monitores actualmente listados. */
  private _monitores: MonitoreoUsuarioResponse[] = [];

  constructor() {
    this._clienteId =
      serviceProvider.getFincaSelectedService().getRequired().clienteId;
  }

  /** HTML de la card; las filas se inyectan al terminar la carga. */
  render(): string {
    return mustache.render(template, { id: this._id });
  }

  /** Resuelve los elementos, engancha la delegación de eventos y carga la lista. */
  connect(): void {
    this._card = document.getElementById(`mcMonitores-${this._id}`);
    this._rows = document.getElementById(`mcMonitorRows-${this._id}`);

    this._card?.addEventListener("click", this._onClick);
    this._card?.addEventListener("change", this._onChange);

    this._cargar();
  }

  dispose(): void {
    this._disposed = true;
    this._card?.removeEventListener("click", this._onClick);
    this._card?.removeEventListener("change", this._onChange);
    this._altaModal?.dispose();
    this._altaModal = null;
  }

  /** Elemento raíz de la card (delegación de eventos). */
  private _card: HTMLElement | null = null;
  /** Contenedor de las filas de monitor. */
  private _rows: HTMLElement | null = null;
  /** Modal de alta abierto, si lo hay (se cierra al desmontar la sección). */
  private _altaModal: MonitorAltaModal | null = null;

  /** Marca de componente desmontado: nada debe tocar el DOM después. */
  private _disposed: boolean = false;

  /** Carga los monitores del cliente y pinta la lista. */
  private _cargar(): void {
    fx_createMonitoreoUsuarioGrpcService(this._clienteId)
      .getListAsync()
      .then((monitores) => {
        if (this._disposed) return;
        this._monitores = monitores;
        this._pintarLista();
      })
      .catch((error) => {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("Error al cargar los monitores", error);
      });
  }

  /** Rearma las filas con el estado actual. El formulario de alta queda
   *  intacto: vive fuera del contenedor de filas justo para eso. */
  private _pintarLista(): void {
    if (this._disposed || !this._rows) return;

    this._rows.innerHTML = mustache.render(rowsTemplate, {
      monitores: this._monitores.map((monitor) => ({
        id: monitor.id,
        nombre: monitor.nombre,
        email: monitor.email,
        pin: monitor.pin,
        iniciales: fx_iniciales(monitor.nombre),
        isActivo: monitor.isActivo,
        ultimoIngresoTexto: monitor.ultimoIngreso
          ? `Último ingreso: ${formatFechaHoraAnioMesDia(monitor.ultimoIngreso)}`
          : "Sin ingresos aún",
      })),
    });
  }

  /** Delegación de clicks: botones de la card y de cada fila. */
  private _onClick = (e: MouseEvent): void => {
    const elemento = (e.target as HTMLElement).closest<HTMLElement>(
      "[data-accion]",
    );
    if (!elemento) return;

    switch (elemento.dataset.accion) {
      case "agregar":
        this._abrirAlta();
        return;
      case "pin":
        this._togglePin(elemento);
        return;
      case "reenviar":
        this._reenviar(elemento as HTMLButtonElement);
        return;
    }
  };

  /** Delegación del switch activo/inactivo (dispara `change`, no `click`). */
  private _onChange = (e: Event): void => {
    const input = e.target as HTMLInputElement;
    if (input.dataset.accion !== "activo") return;

    const monitor = this._monitorDe(input);
    if (!monitor) return;

    input.disabled = true;
    const activar = input.checked;
    const accion = activar
      ? monitoreoUsuarioAccionesGrpcService.activarAsync(monitor)
      : monitoreoUsuarioAccionesGrpcService.inactivarAsync(monitor);

    accion
      .then((actualizado) => {
        if (this._disposed) return;
        this._reemplazar(actualizado);
        this._pintarLista();
        serviceProvider
          .getErrorService()
          .addSuccess(
            TOAST_TITULO,
            `${actualizado.nombre} quedó ${actualizado.isActivo ? "activo" : "inactivo"}.`,
          );
      })
      .catch((error) => {
        if (this._disposed) return;
        // Revierte el switch: el estado real sigue siendo el de antes.
        input.checked = !activar;
        input.disabled = false;
        serviceProvider
          .getErrorService()
          .addError("Error al cambiar el estado del monitor", error);
      });
  };

  /** Abre el modal de alta. El modal se encarga de validar y guardar; acá solo
   *  se recibe el monitor ya creado para sumarlo a la lista. */
  private _abrirAlta(): void {
    this._altaModal = new MonitorAltaModal(this._clienteId, (creado) =>
      this._onCreado(creado),
    );
    this._altaModal.init();
  }

  /** Suma el monitor recién creado a la lista, la reordena y avisa. */
  private _onCreado(creado: MonitoreoUsuarioResponse): void {
    this._altaModal = null;
    if (this._disposed) return;

    this._monitores = [...this._monitores, creado].sort((a, b) =>
      a.nombre.localeCompare(b.nombre),
    );
    this._pintarLista();
    serviceProvider
      .getErrorService()
      .addSuccess(
        TOAST_TITULO,
        `Se le enviaron las credenciales a ${creado.email}.`,
      );
  }

  /** Muestra u oculta el PIN de una fila. */
  private _togglePin(elemento: HTMLElement): void {
    const pin = elemento.dataset.pin ?? "";
    const oculto = elemento.textContent === PIN_OCULTO;
    elemento.textContent = oculto ? pin : PIN_OCULTO;
  }

  /** Reenvía el correo con las credenciales al monitor de la fila. */
  private _reenviar(boton: HTMLButtonElement): void {
    const monitor = this._monitorDe(boton);
    if (!monitor) return;

    boton.disabled = true;
    monitoreoUsuarioAccionesGrpcService
      .reenviarCredencialesAsync(monitor)
      .then(() => {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addSuccess(
            TOAST_TITULO,
            `Se reenviaron las credenciales a ${monitor.email}.`,
          );
      })
      .catch((error) => {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("Error al reenviar las credenciales", error);
      })
      .finally(() => {
        if (this._disposed) return;
        boton.disabled = false;
      });
  }

  /** Monitor al que pertenece la fila que contiene al elemento. */
  private _monitorDe(elemento: HTMLElement): MonitoreoUsuarioResponse | null {
    const id = elemento.closest<HTMLElement>(".sx-list-row")?.dataset.id;
    if (!id) return null;
    return this._monitores.find((monitor) => monitor.id === id) ?? null;
  }

  /** Reemplaza un monitor de la lista local por su versión actualizada. */
  private _reemplazar(actualizado: MonitoreoUsuarioResponse): void {
    this._monitores = this._monitores.map((monitor) =>
      monitor.id === actualizado.id ? actualizado : monitor,
    );
  }
}
